import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { BehaviorSubject, interval, NEVER, Observable, of, race, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, concatMap, filter, map, mapTo, switchMap, take, tap } from 'rxjs/operators';
import { v4 } from 'uuid';
import { getPyodide } from '../helpers/load-pyodide';

import { PYTHON_WORKER_FACTORY } from '../python-worker-factory.token';
import { RunResult } from '../interface';

interface Options {
  warmupSyncRunner: boolean;
}

interface RunParams {
  // через сколько мс прерывать код
  timeout: number;
}

interface QueueItem {
  code: string;
  uuid: string;
  timeout: number;
  forceRunCodeInCurrentStream: boolean;
}

interface ResultsItem {
  consoleOutput: string;
  uuid: string;
  imgBase64?: string;
}

const maxAvailableResults = 100;

const defaultOptions: Options = {
  warmupSyncRunner: true,
};

@Injectable({ providedIn: 'root' })
export class PythonRunnerService {
  private readonly pythonWorkerFactory = inject(PYTHON_WORKER_FACTORY, { optional: true });

  private pyodideWorker: Worker;
  private queue: Subject<QueueItem>;
  private results: ReplaySubject<ResultsItem>;
  private isAsyncPythonInited = new BehaviorSubject(false);
  private isAsyncPythonInited$ = this.isAsyncPythonInited.asObservable();
  private isSyncPythonInited = new BehaviorSubject(false);
  private isSyncPythonInited$ = this.isSyncPythonInited.asObservable();
  private options: Options;

  start(options: Options = defaultOptions): void {
    this.options = options;
    this.initWorker({ kill: false });
    this.initWorkerQueue();
  }

  stop(): void {}

  /**
   * Запускает произвольный python code.
   * Запуски ставятся в очередь и запускаются по очереди, т.к. создавать воркер под каждый запуск долго
   *
   * Перед использованием, проинициализируй сервис через start
   */
  run(
    code: string,
    { timeout }: RunParams = { timeout: 0 },
    forceRunCodeInCurrentStream = false,
  ): Observable<RunResult> {
    const taskUuid = v4();

    this.queue.next({ code, uuid: taskUuid, timeout, forceRunCodeInCurrentStream });

    return this.results.asObservable().pipe(filter(result => result.uuid === taskUuid));
  }

  private initWorkerQueue(): void {
    this.queue = new Subject<QueueItem>();
    this.results = new ReplaySubject<ResultsItem>(maxAvailableResults);

    this.queue
      .asObservable()
      .pipe(
        concatMap(({ code, uuid, timeout, forceRunCodeInCurrentStream }) => {
          const timeout$ =
            timeout > 0
              ? interval(timeout).pipe(
                  mapTo({
                    consoleOutput: `Code execution timeout. Maximum execution time is ${timeout}ms`,
                    imgBase64: undefined,
                  }),
                  tap(() => {
                    /**
                     * Если код выполняется долго, скорее всего там бесконечный цикл
                     * Убиваем текущего воркера и запускаем нового, чтобы восстановиться от бесконечного цикла
                     */
                    this.initWorker({ kill: true });
                  }),
                )
              : NEVER;

          // Ждем пока питон проиницализируется, перед запуском кода
          return (forceRunCodeInCurrentStream ? this.isSyncPythonInited$ : this.isAsyncPythonInited$).pipe(
            filter(isPythonInited => isPythonInited),
            switchMap(() => {
              return race(this.runCode(code, forceRunCodeInCurrentStream), timeout$);
            }),
            map(runResult => ({
              runResult,
              uuid,
            })),
            take(1),
          );
        }),
        takeUntilDestroyed(this, { destroyMethod: this.stop }),
      )
      .subscribe(({ runResult, uuid }) => {
        this.results.next({ ...runResult, uuid });
      });
  }

  private runCode(code: string, forceRunCodeInCurrentStream = false): Observable<RunResult> {
    /**
     * Добавляем обвязку, чтобы возвращать stdout после выполнения python кода
     * По дефолту там возвращается последнее return value
     */
    const codeToRun = `
import sys
import io


old_stdout = sys.stdout
new_stdout = io.StringIO()
sys.stdout = new_stdout

# TODO: Отправлять в js вывод по мере наполнения аутпута?
${code}

new_stdout.getvalue()`;

    if (typeof Worker !== 'undefined' && !forceRunCodeInCurrentStream) {
      return this.runCodeInWorker(codeToRun);
    }

    return this.runCodeInCurrentStream(codeToRun);
  }

  /**
   * Запускает код в отдельном потоке веб воркера
   */
  private runCodeInWorker(code: string): Observable<RunResult> {
    const context = {};
    return new Observable(observer => {
      // eslint-disable-next-line functional/immutable-data
      this.pyodideWorker.onerror = event => {
        observer.error(event.message);
      };
      // eslint-disable-next-line functional/immutable-data
      this.pyodideWorker.onmessage = event => {
        const { results, error } = event.data;

        const consoleOutput = error || results;

        observer.next({ consoleOutput });
        observer.complete();
      };

      this.pyodideWorker.postMessage({
        ...context,
        python: code,
      });

      return {
        unsubscribe: () => {
          /**
           * TODO: заимплементить
           * Отменить выполнение питон кода в текущей версии pyodide нельзя.
           * Вариант убивать всего воркера нам не подходит, потому что воркер долго инициализируется
           */
        },
      };
    });
  }

  /**
   * Запускает код в потоке вместе с js кодом страницы
   */
  private runCodeInCurrentStream(code: string): Observable<RunResult> {
    return getPyodide().pipe(
      switchMap(async pyodide => {
        const dict = pyodide.globals.get('dict');
        const globals = dict();
        await pyodide.loadPackagesFromImports(code);
        if (code.includes('seaborn')) {
          await pyodide.loadPackage('micropip');
          const micropip = pyodide.pyimport('micropip');
          await micropip.install('seaborn');
        }
        const consoleOutput = await pyodide.runPythonAsync(code, { globals });
        const imgBase64 = globals.get('img_base64');
        globals.destroy();
        dict.destroy();

        return { consoleOutput, imgBase64 };
      }),
      catchError(error => {
        console.error(error);
        return of({ consoleOutput: error.message });
      }),
    );
  }

  /**
   * Инициализирует нового веб воркера, если его нет.
   * Если передать kill = true, то принудительно убьет существующий воркер
   */
  private initWorker({ kill } = { kill: false }): void {
    if (!kill && this.pyodideWorker) {
      return;
    }

    if (kill) {
      this.pyodideWorker.terminate();
    }

    this.isAsyncPythonInited.next(false);

    /**
     * Текущая реализация подразумевает, что воркер будет доступен на текущем домене
     * ! Файл сервится отдельно в сторибуке и в приложении:
     * - libs/skypro/blocks/.storybook/static/assets/python-webworker.js
     * - packages/skypro/public/src/assets/python-webworker.js
     *
     */
    if (!this.pythonWorkerFactory) {
      console.error('PYTHON_WORKER_FACTORY is not provided. Register it in skypro-public main.ts or Storybook.');
      return;
    }

    this.pyodideWorker = this.pythonWorkerFactory();

    /**
     * Первый запуск кода очень долгий
     * Прогреваем интерпретатор простой командой в фоне, чтобы к моменту запуска код отработал быстро
     */
    let warmup$: Observable<RunResult | null> = of(null);
    if (this.options.warmupSyncRunner) {
      warmup$ = warmup$.pipe(
        tap(() => console.log('Warmup python sync runner start')),
        switchMap(() =>
          race(
            this.runCode('print(1+1)', true),
            interval(30000).pipe(switchMap(() => throwError(() => new Error('Python sync runner warmup timeout')))),
          ),
        ),
        tap(() => {
          console.log('Warmup python sync runner end');
          this.isSyncPythonInited.next(true);
        }),
        catchError(error => {
          console.error('Python sync runner warmup error', error);
          this.isSyncPythonInited.next(false);
          return of(null);
        }),
      );
    }

    warmup$
      .pipe(
        tap(() => console.log('Warmup python async runner start')),
        switchMap(() => this.runCode('print(1+1)')),
        tap(() => {
          console.log('Warmup python async runner end');
          this.isAsyncPythonInited.next(true);
        }),
        takeUntilDestroyed(this, { destroyMethod: this.stop }),
      )
      .subscribe();
  }
}
